import type { QueryObserverResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { RBACQueryType } from '@protector/entities/rbac/constants';
import { getWorkspaceOperations } from '@protector/entities/rbac/services';
import { getSelectedCompanyId, getSelectedPropertyId, getSystemFlags } from 'core/core.selectors';
import { selectCurrentUser, selectServiceAccount } from 'core/services/auth/auth.reducer';
import { getCurrentWorkspaceId } from 'entities/company/company.selectors';
import { captureError } from 'infra/monitoring/monitoring.utils';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  createOperation,
  enableVerifyRequest,
  formatAllRolesByUser,
  parseWorkspaceOperations,
  setUpResourceByLevel
} from './rbac.functions';
import type {
  GetPermissionUserProps,
  RBACActions,
  RBACAvailableForUserProps,
  RBACDefaultRequestProps,
  RBACLevels,
  RBACOperations,
  RBACAuthorityState,
  RBACParsedOperations,
  RBACSetUpOperationsForRequestProps,
  RBACWorkspaceOperations
} from './rbac.query.model';
import { RBACPermissionTypes } from './rbac.query.model';
import { getAllRolesByUser } from './rbac.service';

const TEN_MINUTES = 1000 * 60 * 10;

export const useGetAllRolesByUser = () => {
  const user = useSelector(selectCurrentUser);

  const { data, isError, isLoading } = useQuery({
    queryKey: [RBACQueryType.GET_ALL_ROLES_BY_USER, user?.id],
    queryFn: async () => {
      const response = await getAllRolesByUser(user?.id);
      if (!response) return undefined;

      return formatAllRolesByUser(response.content);
    },
    staleTime: TEN_MINUTES,
    enabled: !isEmpty(user) && !!user?.id
  });

  return {
    isLoading,
    isError,
    data
  };
};

export const useUserHasSupportAgentRoleInTheSystemContext = () => {
  const { data } = useGetAllRolesByUser();

  return !!data?.system?.SupportAgent?.resource;
};

export const useGetRBACOperationsFullUser = (isLogged: boolean) => {
  return useQuery(
    [RBACQueryType.GET_WORKSPACE_OPERATIONS, isLogged],
    async () => {
      const response = await getWorkspaceOperations([{ action: '*', resource: 'crn:system' }]);

      return parseWorkspaceOperations(response);
    },
    {
      staleTime: TEN_MINUTES,
      enabled: isLogged
    }
  );
};

export const useRBACRequestEnabled = ({
  operations,
  isEnabled = false,
  rbacLevels
}: RBACSetUpOperationsForRequestProps & {
  isEnabled: boolean;
  rbacLevels?: RBACLevels;
}): boolean => {
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const companyId = useSelector(getSelectedCompanyId);
  const propertyId = useSelector(getSelectedPropertyId);

  return enableVerifyRequest({ companyId, propertyId, workspaceId, rbacLevels, operations, isEnabled });
};

export const useGetRBACOperations = ({
  operations,
  isEnabled = false,
  rbacLevels
}: RBACSetUpOperationsForRequestProps & {
  isEnabled: boolean;
  rbacLevels?: RBACLevels;
}): QueryObserverResult<RBACParsedOperations, unknown> => {
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const companyId = useSelector(getSelectedCompanyId);
  const isRequestEnabled = useRBACRequestEnabled({ operations, isEnabled, rbacLevels });

  return useQuery(
    [RBACQueryType.GET_WORKSPACE_OPERATIONS, operations, workspaceId, companyId],
    async () => {
      const response = await getWorkspaceOperations(operations.map(op => ({ action: op.action, resource: op.resource })));

      return parseWorkspaceOperations(response);
    },
    {
      staleTime: TEN_MINUTES,
      enabled: isRequestEnabled
    }
  );
};

export const useVerifyRBACPermissions = ({
  workspaceId,
  companyId,
  propertyId,
  appId,
  isLoading,
  isEnabled = false
}: RBACDefaultRequestProps & { isEnabled: boolean; isLoading: boolean }) => {
  const verifyPermission = useCallback(
    (operations: RBACWorkspaceOperations, level: RBACLevels, action: RBACActions, permissions: RBACPermissionTypes[]) => {
      if (isLoading) return undefined;

      if (!isEnabled) {
        return [];
      }

      const resourceByLevel = setUpResourceByLevel({
        level,
        workspaceId,
        companyId,
        propertyId,
        appId
      });

      if (!operations?.[resourceByLevel]?.[action]?.length) return [];

      return permissions.filter(permission => {
        return operations[resourceByLevel][action].includes(permission);
      });
    },
    [appId, companyId, propertyId, workspaceId, isEnabled, isLoading]
  );

  return {
    verifyPermission
  };
};

export const useValidateRBACAvailableForUser = (): RBACAvailableForUserProps => {
  const user = useSelector(selectCurrentUser);
  const systemFlags = useSelector(getSystemFlags);
  const availableRBAC = systemFlags?.P40_29585_integrate_web_panel_with_RBAC;

  const verifyRBACAvailableForUser = useCallback(() => {
    if (!user || isEmpty(user) || !user?.is_using_rbac || !availableRBAC) return false;

    return !!(user.is_using_rbac && availableRBAC);
  }, [availableRBAC, user]);

  return {
    verifyRBACAvailableForUser
  };
};

export const useGetRBACAuthorityUserState = ({
  rbacActions,
  rbacLevels,
  rbacPermission,
  invalidCallback
}: GetPermissionUserProps): RBACAuthorityState => {
  const { verifyRBACAvailableForUser } = useValidateRBACAvailableForUser();
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const propertyId = useSelector(getSelectedPropertyId);
  const companyId = useSelector(getSelectedCompanyId);
  const isUserAccountService = useSelector(selectServiceAccount);

  const operations =
    !rbacLevels || !rbacActions || !rbacPermission
      ? []
      : rbacPermission?.reduce((acc: RBACOperations[], permission: RBACPermissionTypes) => {
          if (isUserAccountService && permission === RBACPermissionTypes.NOT_READ) {
            return acc;
          }

          acc.push(
            createOperation({
              companyId,
              action: rbacActions,
              level: rbacLevels,
              workspaceId,
              propertyId,
              permission
            })
          );

          return acc;
        }, []);

  const isEnabledForUser = verifyRBACAvailableForUser();
  const isRequestEnabled = useRBACRequestEnabled({ operations, isEnabled: isEnabledForUser, rbacLevels });

  const { data, isLoading, isFetching, isError, error } = useGetRBACOperations({
    operations,
    isEnabled: isEnabledForUser,
    rbacLevels
  });

  const { verifyPermission } = useVerifyRBACPermissions({
    workspaceId,
    companyId,
    propertyId,
    isLoading,
    isEnabled: isEnabledForUser
  });

  useEffect(() => {
    if (!isError) {
      return;
    }

    captureError(error, {
      domain: 'Auth',
      rbacAction: rbacActions ?? '',
      rbacLevel: rbacLevels ?? '',
      workspaceId: workspaceId ?? '',
      companyId: companyId ?? ''
    });
  }, [isError, error, rbacActions, rbacLevels, workspaceId, companyId]);

  /* An error is not an answer of "no grant", and this callback sends the reader to /unlicensed. */
  if (isEmpty(data?.allowed_operations) && !isLoading && !isError && invalidCallback) {
    invalidCallback();
  }

  const allowedOperations = data?.allowed_operations;
  const hasAnswered = Boolean(isEnabledForUser && allowedOperations && rbacLevels && rbacActions && rbacPermission && !isLoading);

  const authority = useMemo(
    () =>
      hasAnswered && allowedOperations && rbacLevels && rbacActions && rbacPermission
        ? verifyPermission(allowedOperations, rbacLevels, rbacActions, rbacPermission)
        : undefined,
    [hasAnswered, allowedOperations, rbacLevels, rbacActions, rbacPermission, verifyPermission]
  );

  /* The request cannot go out until the ids its resource is built from are in the store. */
  const isAwaitingScope = isEnabledForUser && operations.length > 0 && !isRequestEnabled;

  return {
    authority,
    isPending: (isLoading && isFetching) || isAwaitingScope,
    isError
  };
};

export const useGetRBACAuthorityUser = (props: GetPermissionUserProps): RBACPermissionTypes[] | undefined =>
  useGetRBACAuthorityUserState(props).authority;
